import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { ACADEMIC_WEEKS, clampWeekIndex, closureForDate, weekIndexForDate } from '../lib/calendar.ts';
import { addDays, parseLocalDate } from '../lib/dates.ts';
import { deleteHandwriting, putHandwriting } from '../lib/handwriting.ts';
import { ABSENT } from '../lib/bulletin.ts';
import { uid } from '../lib/id.ts';
import { RITUAL_COLOR, subjectColor } from '../lib/subjects.ts';
import { createDefaults, newMeta } from './defaults.ts';
import { normalizeAppData } from './migrate.ts';
import { createLegacyAwareStorage, STORAGE_KEY } from './storage.ts';
import type {
  AppData,
  CollectionName,
  Entity,
  Evaluation,
  Lesson,
  Meeting,
  Observation,
  Settings,
  Skill,
  SlotIndex,
  Student,
  SyncMeta,
  Task,
  WeekTemplateEntry,
} from './types.ts';
import type { DayIndex } from '../lib/calendar.ts';
import { attendusByIds } from '../lib/referentiel.ts';
import { withCopiedLink, type LessonLinked } from '../lib/lesson-links.ts';

/** Bumped whenever the persisted shape changes; drives `migrate`. */
const PERSIST_VERSION = 14;

/** Stamp a row as locally modified. Sync picks it up from here. */
function touch<T extends SyncMeta>(row: T): T {
  return { ...row, updatedAt: new Date().toISOString(), dirty: true };
}

/** Mark a row deleted without removing it, so the deletion can propagate. */
function softDelete<T extends SyncMeta>(row: T): T {
  return { ...row, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString(), dirty: true };
}

/** Rows a user should actually see. */
export function live<T extends SyncMeta>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.deletedAt === null);
}

/** Preserve the order the teacher picked in, without repeats. */
function dedupe(ids: readonly string[]): string[] {
  return [...new Set(ids)];
}

// ------------------------------------------------------------ lesson payload

export interface LessonInput {
  day: DayIndex;
  slot: SlotIndex;
  subject: string;
  domain?: string;
  lessonNumber?: string;
  title: string;
  detail: string;
  /** Ids of the tronc commun attendus chosen in the form. */
  competenceIds: string[];
  material: string;
  differentiation: string;
}

function lessonFields(input: LessonInput) {
  return {
    day: input.day,
    slot: input.slot,
    subject: input.subject,
    domain: input.domain?.trim() ?? '',
    lessonNumber: input.lessonNumber?.trim() ?? '',
    color: input.slot === -1 ? RITUAL_COLOR : subjectColor(input.subject),
    title: input.title.trim() || (input.slot === -1 ? 'Rituels' : input.subject),
    detail: input.detail.trim(),
    competenceIds: dedupe(input.competenceIds),
    // Derived from the chosen attendus rather than typed: the JSON backup has
    // to stay readable by the original app, which only knows this field.
    competence: attendusByIds(dedupe(input.competenceIds))
      .map((a) => a.text)
      .join('\n'),
    material: input.material.trim(),
    differentiation: input.differentiation.trim(),
  };
}

/** The fields an observation form fills in — the stylus PNG is handled apart. */
export type ObservationInput = Pick<
  Observation,
  'studentId' | 'date' | 'lessonId' | 'subject' | 'difficulty' | 'note' | 'support'
>;

/** Everything a column of the class grid shares, whoever the pupil is. */
export type EvaluationCommon = Pick<
  Evaluation,
  'subject' | 'domain' | 'lessonId' | 'title' | 'max' | 'period' | 'date'
>;

/**
 * A mark as it should be stored.
 *
 * Blank is a zero: a pupil who sat the test and scored nothing. A lowercase
 * "a" is folded up, so an absence reads the same however it was typed — the
 * averages ignore case already, but a JSON backup should not be a lucky dip.
 */
function normalizeScore(raw: string): string {
  const text = raw.trim();
  if (text === '') return '0';
  return text.toUpperCase() === ABSENT ? ABSENT : text;
}

/**
 * Whether a stored row belongs to the column being written.
 *
 * Mirrors `columnKey` in `src/lib/bulletin.ts`: a lesson identifies a column
 * on its own, and everything else falls back to what names the test.
 */
function sameColumn(row: Evaluation, common: EvaluationCommon): boolean {
  if (common.lessonId !== '') {
    return row.lessonId === common.lessonId && row.period === common.period;
  }
  return (
    row.lessonId === '' &&
    row.subject === common.subject &&
    row.domain === common.domain &&
    row.period === common.period &&
    row.title === common.title
  );
}

/**
 * The lesson copies a column is attached to, so a row written into it later —
 * marks encoded again, or a pupil scored late — shows up on them too.
 */
function columnExtraLessonIds(rows: readonly Evaluation[], common: EvaluationCommon): string[] {
  return dedupe(
    rows
      .filter((r) => r.deletedAt === null && sameColumn(r, common))
      .flatMap((r) => r.extraLessonIds),
  );
}

/** Soft-delete whatever occupies a cell, except `keepId`: one lesson per cell. */
function clearCell(
  lessons: readonly Lesson[],
  weekStart: string,
  day: DayIndex,
  slot: SlotIndex,
  keepId: string | null,
): Lesson[] {
  return lessons.map((l) =>
    l.id !== keepId &&
    l.deletedAt === null &&
    l.weekStart === weekStart &&
    l.day === day &&
    l.slot === slot
      ? softDelete(l)
      : l,
  );
}

/**
 * Attach every live record of `sourceId` to its copy.
 *
 * Each record changed is marked dirty: the link is an edit to that record, and
 * one left clean would never reach the other device.
 */
function attachCopy<T extends Entity & LessonLinked>(
  rows: readonly T[],
  sourceId: string,
  copyId: string,
): T[] {
  return rows.map((row) => {
    if (row.deletedAt !== null) return row;
    const linked = withCopiedLink(row, sourceId, copyId);
    return linked ? touch(linked) : row;
  });
}

// -------------------------------------------------------------------- state

interface UiState {
  /** Index into ACADEMIC_WEEKS. Not persisted: always opens on today. */
  currentWeekIndex: number;
  /** Student whose file is open in the Classe page. Not persisted. */
  selectedStudentId: string | null;
}

interface Actions {
  // Week navigation
  setWeekIndex: (index: number) => void;
  moveWeek: (delta: number) => void;
  goToTodayWeek: () => void;
  currentWeekStart: () => string;
  currentMonday: () => Date;

  // Settings
  saveSettings: (patch: Pick<Settings, 'teacher' | 'className' | 'schoolYear'>) => void;

  // Students
  selectStudent: (id: string | null) => void;
  saveStudent: (
    id: string | null,
    patch: Pick<Student, 'name' | 'firstName' | 'lastName' | 'birth' | 'philo'>,
  ) => string;
  deleteStudent: (id: string) => void;
  bulkAddStudents: (names: readonly string[]) => number;

  // Tasks & post-its
  addTask: (text: string, due: string) => void;
  updateTask: (id: string, patch: Pick<Task, 'text' | 'due'>) => void;
  toggleTask: (id: string) => void;
  deleteTask: (id: string) => void;
  addPostit: (text: string, color: string) => void;
  deletePostit: (id: string) => void;

  // Lessons
  saveLesson: (id: string | null, input: LessonInput) => void;
  deleteLesson: (id: string) => void;
  /** Id of the copy, or null when the source or the target day is unusable. */
  duplicateLesson: (
    sourceId: string,
    weekStart: string,
    input: LessonInput,
    carryLinks: boolean,
  ) => string | null;
  prepareWeek: (days: readonly DayIndex[], input: Omit<LessonInput, 'day'>) => number;
  copyDay: (from: DayIndex, to: DayIndex) => void;
  /** `weekStart` defaults to the week on screen. */
  hasLessonAt: (
    day: DayIndex,
    slot: SlotIndex,
    exceptId?: string | null,
    weekStart?: string,
  ) => boolean;

  // Week template
  saveWeekTemplate: () => number;
  loadWeekTemplate: () => number;

  // Observations
  addObservation: (input: ObservationInput, handwritingDataUrl: string | null) => void;
  updateObservation: (id: string, input: ObservationInput) => void;

  // Evaluations
  addEvaluation: (
    input: Pick<
      Evaluation,
      'studentId' | 'subject' | 'domain' | 'lessonId' | 'title' | 'score' | 'max' | 'period' | 'date'
    >,
  ) => void;
  addLessonEvaluations: (
    common: EvaluationCommon,
    scores: readonly { studentId: string; score: string }[],
  ) => number;
  setEvaluationScore: (id: string, score: string) => void;
  addStudentScore: (common: EvaluationCommon, studentId: string, score: string) => void;

  // Skills & meetings
  addSkill: (input: Pick<Skill, 'subject' | 'status' | 'title' | 'studentId'>) => void;
  addMeeting: (input: Pick<Meeting, 'date' | 'title' | 'notes'>) => void;

  // Backup
  replaceAll: (data: AppData) => void;
}

export type Store = AppData & UiState & Actions;

// ------------------------------------------------------------------- store

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      ...createDefaults(),

      currentWeekIndex: weekIndexForDate(new Date()),
      selectedStudentId: null,

      // --- week navigation ---------------------------------------------
      setWeekIndex: (index) => set({ currentWeekIndex: clampWeekIndex(index) }),
      moveWeek: (delta) => set((s) => ({ currentWeekIndex: clampWeekIndex(s.currentWeekIndex + delta) })),
      goToTodayWeek: () => set({ currentWeekIndex: weekIndexForDate(new Date()) }),
      currentWeekStart: () => ACADEMIC_WEEKS[get().currentWeekIndex] ?? ACADEMIC_WEEKS[0]!,
      currentMonday: () => parseLocalDate(get().currentWeekStart()),

      // --- settings -----------------------------------------------------
      saveSettings: (patch) => set((s) => ({ settings: touch({ ...s.settings, ...patch }) })),

      // --- students -----------------------------------------------------
      selectStudent: (id) => set({ selectedStudentId: id }),

      saveStudent: (id, patch) => {
        const existing = id ? get().students.find((s) => s.id === id) : undefined;

        if (existing) {
          set((s) => ({
            students: s.students.map((x) => (x.id === id ? touch({ ...x, ...patch }) : x)),
          }));
          return existing.id;
        }

        const created: Student = { id: uid(), ...patch, ...newMeta() };
        set((s) => ({ students: [...s.students, created], selectedStudentId: created.id }));
        return created.id;
      },

      deleteStudent: (id) =>
        set((s) => ({
          // Everything filed under the student goes with them, or the Suivi
          // page keeps counting records nobody can reach.
          students: s.students.map((x) => (x.id === id ? softDelete(x) : x)),
          observations: s.observations.map((x) => (x.studentId === id ? softDelete(x) : x)),
          evaluations: s.evaluations.map((x) => (x.studentId === id ? softDelete(x) : x)),
          skills: s.skills.map((x) => (x.studentId === id ? softDelete(x) : x)),
          selectedStudentId: s.selectedStudentId === id ? null : s.selectedStudentId,
        })),

      bulkAddStudents: (names) => {
        const existing = new Set(
          live(get().students).map((s) => s.name.trim().toLowerCase()),
        );
        const created: Student[] = [];

        for (const raw of names) {
          const name = raw.trim();
          if (!name || existing.has(name.toLowerCase())) continue;
          existing.add(name.toLowerCase());
          created.push({
            id: uid(),
            name,
            firstName: '',
            lastName: '',
            birth: '',
            philo: '',
            ...newMeta(),
          });
        }

        if (created.length) set((s) => ({ students: [...s.students, ...created] }));
        return created.length;
      },

      // --- tasks & post-its ---------------------------------------------
      addTask: (text, due) =>
        set((s) => ({
          tasks: [...s.tasks, { id: uid(), text: text.trim(), due, done: false, ...newMeta() }],
        })),

      updateTask: (id, patch) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === id ? touch({ ...t, text: patch.text.trim(), due: patch.due }) : t,
          ),
        })),

      toggleTask: (id) =>
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === id ? touch({ ...t, done: !t.done }) : t)),
        })),

      deleteTask: (id) =>
        set((s) => ({
          tasks: s.tasks.map((task) => (task.id === id ? softDelete(task) : task)),
        })),

      addPostit: (text, color) =>
        set((s) => ({
          postits: [{ id: uid(), text: text.trim(), color, ...newMeta() }, ...s.postits],
        })),

      deletePostit: (id) =>
        set((s) => ({
          postits: s.postits.map((postit) => (postit.id === id ? softDelete(postit) : postit)),
        })),

      // --- lessons -------------------------------------------------------
      hasLessonAt: (day, slot, exceptId = null, weekStart = get().currentWeekStart()) =>
        live(get().lessons).some(
          (l) => l.id !== exceptId && l.weekStart === weekStart && l.day === day && l.slot === slot,
        ),

      saveLesson: (id, input) => {
        const week = get().currentWeekStart();
        const fields = lessonFields(input);

        set((s) => {
          // One lesson per cell: an occupant other than the one being edited
          // is replaced. The UI confirms with the user before calling this.
          const lessons = clearCell(s.lessons, week, fields.day, fields.slot, id);

          const existing = id ? lessons.find((l) => l.id === id) : undefined;
          if (existing) {
            return {
              lessons: lessons.map((l) =>
                l.id === id ? touch({ ...l, ...fields, weekStart: week }) : l,
              ),
            };
          }

          const created: Lesson = { id: uid(), weekStart: week, ...fields, ...newMeta() };
          return { lessons: [...lessons, created] };
        });
      },

      deleteLesson: (id) =>
        set((s) => ({ lessons: s.lessons.map((l) => (l.id === id ? softDelete(l) : l)) })),

      /**
       * A copy of a lesson on any school day of the year, not only the week on
       * screen — attached, when the teacher asks, to what was recorded during
       * the original.
       *
       * The observations and marks are linked rather than cloned (see
       * `src/lib/lesson-links.ts`): a note is one thing that happened, and a
       * cloned mark would count twice in the report.
       */
      duplicateLesson: (sourceId, weekStart, input, carryLinks) => {
        if (!get().lessons.some((l) => l.id === sourceId)) return null;
        if (!ACADEMIC_WEEKS.includes(weekStart)) return null;
        if (closureForDate(addDays(parseLocalDate(weekStart), input.day))) return null;

        const fields = lessonFields(input);
        const copyId = uid();

        set((s) => ({
          lessons: [
            ...clearCell(s.lessons, weekStart, fields.day, fields.slot, null),
            { id: copyId, weekStart, ...fields, ...newMeta() },
          ],
          ...(carryLinks
            ? {
                observations: attachCopy(s.observations, sourceId, copyId),
                evaluations: attachCopy(s.evaluations, sourceId, copyId),
              }
            : {}),
        }));

        return copyId;
      },

      prepareWeek: (days, input) => {
        const week = get().currentWeekStart();
        const monday = parseLocalDate(week);

        // Never drop a lesson onto a closed day.
        const targets = days.filter((d) => closureForDate(addDays(monday, d)) === null);
        if (!targets.length) return 0;

        set((s) => {
          let lessons = s.lessons;

          for (const day of targets) {
            const fields = lessonFields({ ...input, day });

            lessons = lessons.map((l) =>
              l.deletedAt === null &&
              l.weekStart === week &&
              l.day === day &&
              l.slot === fields.slot
                ? softDelete(l)
                : l,
            );

            lessons = [...lessons, { id: uid(), weekStart: week, ...fields, ...newMeta() }];
          }

          return { lessons };
        });

        return targets.length;
      },

      copyDay: (from, to) => {
        const week = get().currentWeekStart();

        set((s) => {
          const source = live(s.lessons).filter((l) => l.weekStart === week && l.day === from);

          const cleared = s.lessons.map((l) =>
            l.deletedAt === null && l.weekStart === week && l.day === to ? softDelete(l) : l,
          );

          const copies: Lesson[] = source.map((l) => ({
            ...l,
            id: uid(),
            day: to,
            ...newMeta(),
          }));

          return { lessons: [...cleared, ...copies] };
        });
      },

      // --- week template --------------------------------------------------
      saveWeekTemplate: () => {
        const week = get().currentWeekStart();
        const source = live(get().lessons).filter((l) => l.weekStart === week);

        set((s) => ({
          // Replace wholesale: the template is a snapshot, not an accumulation.
          weekTemplate: [
            ...s.weekTemplate.map((e) => (e.deletedAt === null ? softDelete(e) : e)),
            ...source.map(
              (l): WeekTemplateEntry => ({
                id: uid(),
                day: l.day,
                slot: l.slot,
                subject: l.subject,
                domain: l.domain,
                color: l.color,
                lessonNumber: l.lessonNumber,
                title: l.title,
                detail: l.detail,
                competenceIds: [...l.competenceIds],
                competence: l.competence,
                material: l.material,
                differentiation: l.differentiation,
                ...newMeta(),
              }),
            ),
          ],
        }));

        return source.length;
      },

      loadWeekTemplate: () => {
        const week = get().currentWeekStart();
        const monday = parseLocalDate(week);
        const template = live(get().weekTemplate);
        if (!template.length) return 0;

        // Skip entries landing on a closed day rather than hiding them there.
        const applicable = template.filter((e) => closureForDate(addDays(monday, e.day)) === null);

        set((s) => ({
          lessons: [
            ...s.lessons.map((l) =>
              l.deletedAt === null && l.weekStart === week ? softDelete(l) : l,
            ),
            ...applicable.map(
              (e): Lesson => ({
                id: uid(),
                weekStart: week,
                day: e.day,
                slot: e.slot,
                subject: e.subject,
                domain: e.domain,
                color: e.color,
                lessonNumber: e.lessonNumber,
                title: e.title,
                detail: e.detail,
                competenceIds: [...e.competenceIds],
                competence: e.competence,
                material: e.material,
                differentiation: e.differentiation,
                ...newMeta(),
              }),
            ),
          ],
        }));

        return applicable.length;
      },

      // --- observations ----------------------------------------------------
      addObservation: (input, handwritingDataUrl) => {
        const observation: Observation = {
          id: uid(),
          ...input,
          extraLessonIds: [],
          note: input.note.trim() || 'Note manuscrite',
          hasHandwriting: handwritingDataUrl !== null,
          handwritingPath: null,
          ...newMeta(),
        };

        if (handwritingDataUrl) {
          // Fire-and-forget: the PNG lives in IndexedDB, never in the store.
          void putHandwriting(observation.id, handwritingDataUrl);
        }

        set((s) => ({
          observations: [...s.observations, observation],
          selectedStudentId: input.studentId,
        }));
      },

      /**
       * Correct a note that was filed too fast.
       *
       * The quick note guesses the pupil, the lesson and the subject from one
       * typed line, so it is going to guess wrong sometimes; without this the
       * only fix was to live with it. The stylus PNG is deliberately left
       * alone: it is keyed on the observation id and lives outside the store,
       * and nothing here can change which drawing belongs to which note.
       */
      updateObservation: (id, input) =>
        set((s) => ({
          observations: s.observations.map((o) =>
            o.id === id
              ? touch({ ...o, ...input, note: input.note.trim() || 'Note manuscrite' })
              : o,
          ),
        })),

      // --- evaluations ------------------------------------------------------
      addEvaluation: (input) =>
        set((s) => ({
          evaluations: [...s.evaluations, { id: uid(), ...input, extraLessonIds: [], ...newMeta() }],
          selectedStudentId: input.studentId,
        })),

      /**
       * One row per pupil, blanks included.
       *
       * The dialog this replaces skipped empty inputs, which quietly left the
       * pupil out of the average instead of scoring them zero. Here a blank is
       * a zero, as it is on paper; a pupil who was away gets "A".
       *
       * Encoding the same test twice corrects it rather than doubling it: the
       * previous rows are soft-deleted first, the same way `saveLesson`
       * replaces the occupant of a cell. Two sets of marks under one heading
       * would show as one column with half the results silently dropped.
       */
      addLessonEvaluations: (common, scores) => {
        if (!scores.length) return 0;

        set((s) => {
          // Read before the old rows go: they know which copies the column is on.
          const extraLessonIds = columnExtraLessonIds(s.evaluations, common);
          return {
            evaluations: [
              ...s.evaluations.map((e) =>
                e.deletedAt === null && sameColumn(e, common) ? softDelete(e) : e,
              ),
              ...scores.map(
                (x): Evaluation => ({
                  id: uid(),
                  studentId: x.studentId,
                  score: normalizeScore(x.score),
                  ...common,
                  extraLessonIds: [...extraLessonIds],
                  ...newMeta(),
                }),
              ),
            ],
          };
        });

        return scores.length;
      },

      setEvaluationScore: (id, score) =>
        set((s) => ({
          evaluations: s.evaluations.map((e) =>
            e.id === id ? touch({ ...e, score: normalizeScore(score) }) : e,
          ),
        })),

      addStudentScore: (common, studentId, score) =>
        set((s) => ({
          evaluations: [
            ...s.evaluations,
            {
              id: uid(),
              studentId,
              score: normalizeScore(score),
              ...common,
              extraLessonIds: columnExtraLessonIds(s.evaluations, common),
              ...newMeta(),
            },
          ],
        })),

      // --- skills & meetings -------------------------------------------------
      addSkill: (input) =>
        set((s) => ({ skills: [...s.skills, { id: uid(), ...input, ...newMeta() }] })),

      addMeeting: (input) =>
        set((s) => ({
          meetings: [
            ...s.meetings,
            {
              id: uid(),
              date: input.date,
              title: input.title.trim(),
              notes: input.notes.trim(),
              ...newMeta(),
            },
          ],
        })),

      // --- backup ------------------------------------------------------------
      replaceAll: (data) => set({ ...data, selectedStudentId: null }),
    }),

    {
      name: STORAGE_KEY,
      version: PERSIST_VERSION,
      storage: createJSONStorage(() => createLegacyAwareStorage(localStorage)),

      // Only the data is persisted; week position and selection are per-session.
      partialize: (s): AppData => ({
        settings: s.settings,
        students: s.students,
        tasks: s.tasks,
        postits: s.postits,
        lessons: s.lessons,
        observations: s.observations,
        evaluations: s.evaluations,
        meetings: s.meetings,
        skills: s.skills,
        weekTemplate: s.weekTemplate,
      }),

      migrate: async (persisted) => {
        const { data, handwriting } = normalizeAppData(persisted);

        // Inline PNGs from the old format move to IndexedDB before the store
        // goes live, so no render ever sees a data-URL in state again.
        await Promise.all(
          Object.entries(handwriting).map(([id, dataUrl]) => putHandwriting(id, dataUrl)),
        );

        return data as never;
      },
    },
  ),
);

// ------------------------------------------------------------------ helpers

/** Drop a handwriting image along with its observation. */
export async function purgeHandwriting(observationId: string): Promise<void> {
  await deleteHandwriting(observationId);
}

/** Every collection as a plain array, for the sync engine to walk. */
export function collectionRows(data: AppData, name: CollectionName): Entity[] {
  return data[name];
}
